import React, { useState, useEffect } from 'react';
import { Send, RefreshCw, Copy, Check, Clock, Trash2, Menu, X, CreditCard, LogOut } from 'lucide-react';
import { supabase } from './lib/supabase';
import Landing from './Landing';

export default function MeetingFollowUp() {
  const API_URL = '/api';
  const TOKEN_LIMIT_FREE = 10000;
  const TOKEN_LIMIT_PRO = 50000;
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [notes, setNotes] = useState('');
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showLanding, setShowLanding] = useState(true);
  const [originalNotes, setOriginalNotes] = useState('');
  const [result, setResult] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [variant, setVariant] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');

  // Combined function to load or create user
  const loadUserDataOrCreate = async (userId, email) => {
    try {
      console.log('🔍 Loading user data for:', userId);
      
      // First try to load from database with timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Database timeout')), 5000)
      );
      
      const loadPromise = supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      
      const { data, error } = await Promise.race([loadPromise, timeoutPromise]);

      if (error) {
        if (error.code === 'PGRST116' || error.code === '406' || error.message === 'Database timeout') {
          console.log('👤 User not found or timeout, creating via API...');
          await createUserViaAPI(userId, email);
        } else {
          throw error;
        }
      } else {
        console.log('✅ User loaded from database:', data);
        setUser(data);
        loadProjects(userId);
        setLoading(false);
      }
    } catch (err) {
      console.error('❌ Error loading user:', err);
      await createUserViaAPI(userId, email);
    }
  };

  // Create user via API and use response directly (no database read)
  const createUserViaAPI = async (userId, email) => {
    try {
      console.log('📝 Creating user via API:', email);
      
      const response = await fetch(`${API_URL}/create-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, email })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'API failed');
      }

      const result = await response.json();
      console.log('✅ User created! Using API response directly');
      
      // Use the data from API response directly - don't try to read from database
      setUser(result.user);
      loadProjects(userId);
      setLoading(false);
      
    } catch (err) {
      console.error('❌ Failed to create user via API:', err);
      setError('Failed to create account. Please refresh and try again.');
      setLoading(false);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        loadUserDataOrCreate(session.user.id, session.user.email);
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      if (session) {
        await loadUserDataOrCreate(session.user.id, session.user.email);
      } else {
        setUser(null);
        setProjects([]);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user's meetings from database
  const loadProjects = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('meetings')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  // Save meeting to database
  const saveProject = async (projectData) => {
    if (!session) {
      console.log('⚠️ No session, skipping save');
      return;
    }

    // Check if this meeting already exists (prevent duplicates)
    const existingMeeting = projects.find(p => p.title === (projectData.emailSubjectLine || 'Untitled Meeting'));
    if (existingMeeting) {
      console.log('⚠️ Meeting already saved, skipping duplicate');
      return;
    }

    try {
      console.log('💾 Saving new meeting to database...');
      
      const { data, error } = await supabase
        .from('meetings')
        .insert([
          {
            user_id: session.user.id,
            title: projectData.emailSubjectLine || 'Untitled Meeting',
            notes: notes,
            data: projectData
          }
        ])
        .select()
        .single();

      if (error) throw error;
      
      console.log('✅ Meeting saved successfully');
      setProjects([data, ...projects]);
    } catch (err) {
      console.error('❌ Error saving project:', err);
      // Don't show error to user - saving is not critical
    }
  };

  // Delete meeting from database
  const deleteProject = async (projectId) => {
    try {
      const { error } = await supabase
        .from('meetings')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      const updatedProjects = projects.filter(p => p.id !== projectId);
      setProjects(updatedProjects);
      
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
        setResult(null);
        setVariant(null);
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Failed to delete meeting');
    }
  };

  // Update token usage in database
  const updateTokenUsage = async (tokensUsed) => {
    if (!user) return;

    try {
      const newTokensUsed = user.tokens_used + tokensUsed;
      
      const { error } = await supabase
        .from('users')
        .update({ tokens_used: newTokensUsed })
        .eq('id', user.id);

      if (error) throw error;

      setUser({ ...user, tokens_used: newTokensUsed });
    } catch (err) {
      console.error('Error updating tokens:', err);
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: authEmail,
        password: authPassword,
      });

      if (error) throw error;
      
      setAuthEmail('');
      setAuthPassword('');
    } catch (err) {
      setAuthError(err.message);
    }
  };

  // Handle signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthError('');
    
    try {
      const { error } = await supabase.auth.signUp({
        email: authEmail,
        password: authPassword,
      });

      if (error) throw error;
      
      // User will be auto-logged in (no email confirmation required)
      setAuthEmail('');
      setAuthPassword('');
    } catch (err) {
      setAuthError(err.message);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProjects([]);
    setNotes('');
    setResult(null);
    setVariant(null);
  };

  const systemPrompt = `You are an expert meeting follow-up specialist.
Your job is to help professionals turn messy meeting notes into:
- Clear decisions
- Explicit ownership
- Professional, send-ready follow-up emails

You think like someone who:
- Understands workplace power dynamics
- Avoids ambiguity
- Protects the sender's credibility

CRITICAL RULES:
- Never invent facts
- Never guess owners or deadlines
- If something is unclear, explicitly state "Not specified"
- Be concise but decisive
- Use plain professional business English
- No emojis, filler, motivational language, apologies, or hedging

Transform meeting notes into STRICT JSON following this schema EXACTLY:
{
  "summary": ["string"],
  "actionItems": [
    {
      "task": "string",
      "owner": "string",
      "deadline": "string"
    }
  ],
  "emailSubjectLine": "string",
  "followUpEmail": "string"
}

Quality checks:
- Email must sound like it was written by a competent professional
- No vague phrases ("let me know", "moving forward", "as discussed")
- Every paragraph must reduce ambiguity
- Email should be safe to forward to leadership or clients

Return ONLY the JSON object. No markdown, no explanations, no extra text.`;

  const variantPrompts = {
    executive: "Regenerate with a more direct and executive tone. Keep all facts, owners, and deadlines identical.",
    client: "Regenerate with a client-friendly but firm tone. Keep all facts, owners, and deadlines identical.",
    internal: "Regenerate for internal team alignment. Keep all facts, owners, and deadlines identical.",
    cautious: "Regenerate with a risk-aware and cautious tone. Keep all facts, owners, and deadlines identical."
  };

  const processResponse = async (userMessage, isVariant = false) => {
    if (!user) return;
    
    if (user.tokens_used >= user.token_limit) {
      setShowUpgrade(true);
      setError('Token limit reached. Please upgrade to Pro to continue.');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      console.log('🚀 Sending request to API...');
      
      const response = await fetch(`${API_URL}/process-meeting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          userMessage: userMessage,
          systemPrompt: systemPrompt,
          tokensUsed: user.tokens_used,
          tier: user.tier
        })
      });

      console.log('📥 Response received:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.upgradeRequired) {
          setShowUpgrade(true);
        }
        throw new Error(errorData.error || 'Failed to process');
      }

      const data = await response.json();
      console.log('✅ Data parsed:', data);
      
      const tokensUsed = data.usage.input_tokens + data.usage.output_tokens;
      await updateTokenUsage(tokensUsed);
      
      let cleanJson = data.content.trim();
      cleanJson = cleanJson.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      console.log('🧹 Cleaned JSON:', cleanJson);
      
      let parsed = JSON.parse(cleanJson);

// Decode any URL-encoded text in the response
if (parsed.emailSubjectLine && parsed.emailSubjectLine.includes('%')) {
  parsed.emailSubjectLine = decodeURIComponent(parsed.emailSubjectLine);
}
if (parsed.followUpEmail && parsed.followUpEmail.includes('%')) {
  parsed.followUpEmail = decodeURIComponent(parsed.followUpEmail);
}
if (parsed.summary && Array.isArray(parsed.summary)) {
  parsed.summary = parsed.summary.map(item => 
    item.includes('%') ? decodeURIComponent(item) : item
  );
}
      console.log('📊 Parsed result:', parsed);
      
      // Aggressively clean all URL encoding
      const cleanData = {
        ...parsed,
        emailSubjectLine: decodeURIComponent(parsed.emailSubjectLine || '').replace(/%20/g, ' ').replace(/%0A/g, '\n'),
        followUpEmail: decodeURIComponent(parsed.followUpEmail || '').replace(/%20/g, ' ').replace(/%0A/g, '\n'),
        summary: parsed.summary.map(item => decodeURIComponent(item || '').replace(/%20/g, ' ').replace(/%0A/g, '\n')),
        actionItems: parsed.actionItems.map(item => ({
          ...item,
          task: decodeURIComponent(item.task || '').replace(/%20/g, ' ').replace(/%0A/g, '\n'),
          owner: decodeURIComponent(item.owner || '').replace(/%20/g, ' ').replace(/%0A/g, '\n'),
          deadline: decodeURIComponent(item.deadline || '').replace(/%20/g, ' ').replace(/%0A/g, '\n')
        }))
      };

      if (isVariant) {
        console.log('🎨 Setting variant (not saving to DB)');
        setVariant(cleanData);
      } else {
        console.log('💾 Setting result and saving to DB');
        setResult(cleanData);
        setVariant(null);
        await saveProject(cleanData);
        setOriginalNotes(notes);
        setNotes('');
      }
    } catch (err) {
      console.error('❌ Error in processResponse:', err);
      setError(err.message || 'Failed to process notes');
    } finally {
      console.log('🏁 Setting processing to false');
      setProcessing(false);
    }
  };

  const handleSubmit = () => {
    if (!notes.trim()) {
      setError('Please enter meeting notes');
      return;
    }
    processResponse(notes);
  };

  const handleRegenerate = (variantType) => {
    if (!result || !originalNotes) return; // Check originalNotes exists
    
    const variantPrompt = `${variantPrompts[variantType]}

ORIGINAL MEETING NOTES:
${originalNotes}

You must return ONLY valid JSON in the exact same format as before. No explanations, no conversational text.`;
    
    processResponse(variantPrompt, true);
  };

  const copyToClipboard = async (text) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // iOS MUST use textarea method - modern API doesn't work reliably
    if (isIOS) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        textArea.setSelectionRange(0, text.length);
        
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      } catch (err) {
        console.error('iOS copy failed:', err);
        alert('Copy failed. Please manually select and copy the text.');
        return;
      }
    }
    
    // For all other devices: try modern API first
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }
    } catch (err) {
      console.log('Modern clipboard failed, trying fallback:', err);
    }
    
    // Fallback for non-iOS devices
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        throw new Error('Copy command failed');
      }
    } catch (err) {
      console.error('All copy methods failed:', err);
      alert('Unable to copy. Please manually select and copy the text.');
    }
  };

  const loadProject = (project) => {
    setSelectedProject(project);
    setResult(project.data);
    setNotes(project.notes);
    setVariant(null);
    setShowMenu(false);
  };

  const handleUpgrade = async () => {
    if (!user) return;

    try {
      const response = await fetch(`${API_URL}/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          email: session.user.email
        })
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  // Show landing page for first-time visitors
  if (showLanding && !session) {
    return <Landing onGetStarted={() => setShowLanding(false)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (session && !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading your account...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8">
          <button
            onClick={() => setShowLanding(true)}
            className="text-sm text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-1"
          >
            ← Back
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">MeetingPro</h1>
          <p className="text-gray-600 mb-8 text-center">Transform meeting notes into professional follow-ups</p>
          
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setAuthMode('login')}
              className={`flex-1 py-2 rounded-lg font-medium ${
                authMode === 'login'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setAuthMode('signup')}
              className={`flex-1 py-2 rounded-lg font-medium ${
                authMode === 'signup'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={authMode === 'login' ? handleLogin : handleSignup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>

            {authError && (
              <div className={`p-3 rounded-lg text-sm ${
                authError.includes('Check your email')
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}>
                {authError}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700"
            >
              {authMode === 'login' ? 'Login' : 'Sign Up'}
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-700 font-medium mb-2">Free Plan Includes:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>✓ 10,000 tokens (~5 meetings)</li>
              <li>✓ Save meetings to your account</li>
              <li>✓ Access from any device</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Main app (when user is logged in)
  const displayData = variant || result;
  const tokensRemaining = user ? user.token_limit - user.tokens_used : 0;
  const tokensPercentage = user ? (user.tokens_used / user.token_limit) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">MeetingPro</h1>
            
            {/* Center: Tokens & Badge */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Tokens - Mobile optimized */}
              <div className="flex flex-col items-end">
                <div className="flex items-baseline gap-1">
                  <span className={`text-base sm:text-lg font-semibold ${
                    tokensPercentage > 80 ? 'text-red-600' : 'text-gray-900'
                  }`}>
                    {tokensRemaining.toLocaleString()}
                  </span>
                  <span className="text-xs sm:text-sm text-gray-400">
                    / {user.token_limit.toLocaleString()}
                  </span>
                </div>
                <span className="text-[10px] sm:text-xs text-gray-500">tokens</span>
              </div>
              
              {/* Pro Badge or Upgrade Button */}
              {user.tier === 'pro' ? (
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API_URL}/create-portal-session`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ customerId: user.stripe_customer_id })
                      });
                      const data = await response.json();
                      if (data.url) {
                        window.location.href = data.url;
                      }
                    } catch (err) {
                      alert('Error: ' + err.message);
                    }
                  }}
                  className="px-2 sm:px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap hover:bg-blue-200 transition-colors cursor-pointer"
                >
                  Manage Plan
                </button>
              ) : (
                <button
                  onClick={() => setShowUpgrade(true)}
                  className="px-2 sm:px-4 py-1 sm:py-2 bg-blue-600 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-700 whitespace-nowrap"
                >
                  Upgrade
                </button>
              )}
            </div>
            
            {/* Right: Menu & Logout */}
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Menu"
              >
                {showMenu ? <X className="w-5 h-5 text-gray-700" /> : <Menu className="w-5 h-5 text-gray-700" />}
              </button>
              <button
                onClick={handleLogout}
                className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
                title="Logout"
                aria-label="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 flex gap-6">
        {showMenu && (
          <div className="w-80 bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-fit">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Past Meetings</h2>
            {projects.length === 0 ? (
              <p className="text-sm text-gray-500">No saved meetings yet</p>
            ) : (
              <div className="space-y-2">
                {projects.map(project => (
                  <div
                    key={project.id}
                    className="group relative p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-gray-50 transition-all cursor-pointer"
                  >
                    <div className="flex items-start gap-2 pr-8">
                      <div onClick={() => loadProject(project)} className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate" title={project.title}>
                          {project.title}
                        </p>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          {new Date(project.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Delete this meeting?')) {
                            deleteProject(project.id);
                          }
                        }}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 rounded transition-opacity"
                        aria-label="Delete meeting"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              {user.tier === 'free' ? 'Free Account' : 'Pro Account'}
            </h2>
            <p className="text-gray-600 mb-6">Transform messy meeting notes into professional follow-ups</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meeting Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Paste your meeting notes here..."
                  rows={8}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {tokensPercentage > 80 && user.tier === 'free' && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
                  Warning: You've used {tokensPercentage.toFixed(0)}% of your tokens. Consider upgrading to Pro.
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    if (!result || selectedProject) {
                      setNotes('');
                      setResult(null);
                      setVariant(null);
                      setSelectedProject(null);
                      setError('');
                    } else {
                      if (window.confirm('This will clear your current results. They are already saved in Past Meetings. Continue?')) {
                        setNotes('');
                        setResult(null);
                        setVariant(null);
                        setSelectedProject(null);
                        setError('');
                      }
                    }
                  }}
                  className="w-full sm:flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  New Meeting
                </button>
                
                <button
                  onClick={handleSubmit}
                  disabled={processing || user.tokens_used >= user.token_limit}
                  className="w-full sm:flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Generate Follow-Up
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {displayData && (
            <div className="space-y-6">
              {variant && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-medium">Viewing regenerated variant. Facts and owners remain unchanged.</p>
                </div>
              )}

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Summary</h2>
                <ul className="space-y-2">
                  {displayData.summary.map((item, idx) => (
                    <li key={idx} className="text-gray-700 leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Action Items</h2>
                <div className="space-y-3">
                  {displayData.actionItems.map((item, idx) => (
                    <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2">
                      <p className="text-gray-900 font-medium">{item.task}</p>
                      <div className="flex gap-4 mt-1 text-sm text-gray-600">
                        <span><strong>Owner:</strong> {item.owner}</span>
                        <span><strong>Deadline:</strong> {item.deadline}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Email</h2>
                  <button
                  onClick={() => {
                    try {
                      // Try to decode - if it fails, use original
                      let subject = displayData.emailSubjectLine;
                      let body = displayData.followUpEmail;
                      
                      // Check if text looks URL-encoded (contains %20, %0A, etc.)
                      if (subject.includes('%20') || subject.includes('%0A')) {
                        subject = decodeURIComponent(subject);
                      }
                      if (body.includes('%20') || body.includes('%0A')) {
                        body = decodeURIComponent(body);
                      }
                      
                      const emailText = `Subject: ${subject}\n\n${body}`;
                      copyToClipboard(emailText);
                    } catch (err) {
                      console.error('Decode error:', err);
                      // Fallback: use original text
                      const emailText = `Subject: ${displayData.emailSubjectLine}\n\n${displayData.followUpEmail}`;
                      copyToClipboard(emailText);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy Email'}
                </button>
                </div>
                
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-600 mb-1">Subject:</p>
                  <p className="text-gray-900 font-medium">{displayData.emailSubjectLine}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-600 mb-2">Body:</p>
                  <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap text-gray-800 leading-relaxed">
                    {displayData.followUpEmail}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Regenerate with Different Tone</h2>
                <p className="text-sm text-gray-600 mb-4">Facts, owners, and deadlines will remain identical. Only tone changes.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { key: 'executive', label: 'Executive' },
                    { key: 'client', label: 'Client-Friendly' },
                    { key: 'internal', label: 'Internal Team' },
                    { key: 'cautious', label: 'Risk-Aware' }
                  ].map(v => (
                    <button
                      key={v.key}
                      onClick={() => handleRegenerate(v.key)}
                      disabled={processing || user.tokens_used >= user.token_limit}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 disabled:opacity-50"
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showUpgrade && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">Upgrade to Pro</h2>
              <button onClick={() => setShowUpgrade(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">Current Plan: Free</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">10,000 tokens</p>
                <p className="text-sm text-gray-600">~5 meetings</p>
              </div>
              
              <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-blue-900">Pro Plan</p>
                  <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">Best Value</span>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-1">
                  $12<span className="text-lg text-gray-600">/month</span>
                </p>
                <ul className="text-sm text-gray-700 space-y-1 mt-3">
                  <li>✓ 50,000 tokens (~25 meetings)</li>
                  <li>✓ Tokens reset monthly</li>
                  <li>✓ Unlimited saved meetings</li>
                  <li>✓ Access from any device</li>
                  <li>✓ Priority support</li>
                </ul>
              </div>
            </div>

            <button
              onClick={handleUpgrade}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Upgrade Now with Stripe
            </button>
            <p className="text-xs text-gray-500 text-center mt-4">
              Secure payment processing by Stripe
            </p>
          </div>
        </div>
      )}
      {/* Footer */}
<footer className="bg-white border-t border-gray-200 mt-auto">
  <div className="max-w-7xl mx-auto px-6 py-6">
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
      <p className="text-sm text-gray-600">
        © 2026 MeetingPro. All rights reserved.
      </p>
      <div className="flex gap-6 text-sm">
        <a href="mailto:support@meetingpro.app" className="text-gray-600 hover:text-blue-600">
          Contact
        </a>
        <button onClick={() => setShowTerms(true)} className="text-gray-600 hover:text-blue-600">
          Terms
        </button>
        <button onClick={() => setShowPrivacy(true)} className="text-gray-600 hover:text-blue-600">
          Privacy
        </button>
      </div>
    </div>
  </div>
</footer>
{/* Terms of Service Modal */}
{showTerms && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">Terms of Service</h2>
        <button onClick={() => setShowTerms(false)} className="p-2 hover:bg-gray-100 rounded-lg">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="space-y-4 text-gray-700 text-sm">
        <p className="font-semibold">Last updated: January 13, 2026</p>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">1. Acceptance of Terms</h3>
          <p>By using MeetingPro, you agree to these Terms of Service. If you don't agree, please don't use our service.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">2. Service Description</h3>
          <p>MeetingPro provides AI-powered meeting follow-up generation. We offer a free tier (10,000 tokens) and a Pro tier (50,000 tokens/month for $12/month).</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">3. User Responsibilities</h3>
          <p className="mb-2">You agree to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide accurate information</li>
            <li>Not abuse or misuse the service</li>
            <li>Not share your account credentials</li>
            <li>Use the service only for lawful purposes</li>
            <li>Not attempt to reverse engineer or hack the service</li>
          </ul>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">4. Payment & Subscriptions</h3>
          <p>Pro subscriptions are billed monthly at $12/month via Stripe. You can cancel anytime through your Stripe customer portal. No refunds for partial months. Token limits reset on the 1st of each month.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">5. Service Availability</h3>
          <p>We strive for 99% uptime but don't guarantee uninterrupted service. We may modify, suspend, or discontinue features with reasonable notice.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">6. Data & Privacy</h3>
          <p>Your meeting notes are stored securely and encrypted. We don't sell your data or use it to train AI models. See our Privacy Policy for full details.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">7. Intellectual Property</h3>
          <p>You retain ownership of your meeting notes and generated content. MeetingPro retains all rights to the service itself.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">8. Limitation of Liability</h3>
          <p>MeetingPro is provided "as is" without warranties. We're not liable for any damages arising from use of the service. Maximum liability is limited to fees paid in the last 12 months.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">9. Termination</h3>
          <p>We may terminate accounts that violate these terms. You may delete your account anytime by contacting support.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">10. Changes to Terms</h3>
          <p>We may update these terms. Continued use after changes means you accept the new terms. We'll notify you of significant changes.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">11. Contact</h3>
          <p>Questions? Email us at support@meetingpro.app</p>
        </div>
      </div>
      
      <button
        onClick={() => setShowTerms(false)}
        className="mt-6 w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700"
      >
        Close
      </button>
    </div>
  </div>
)}

{/* Privacy Policy Modal */}
{showPrivacy && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">Privacy Policy</h2>
        <button onClick={() => setShowPrivacy(false)} className="p-2 hover:bg-gray-100 rounded-lg">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="space-y-4 text-gray-700 text-sm">
        <p className="font-semibold">Last updated: January 13, 2026</p>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">1. Information We Collect</h3>
          <p className="mb-2">We collect the following information:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Account Information:</strong> Email address for login and communication</li>
            <li><strong>Meeting Data:</strong> Notes you input and generated follow-ups you create</li>
            <li><strong>Usage Data:</strong> Token usage, feature usage, login timestamps</li>
            <li><strong>Payment Information:</strong> Processed securely by Stripe (we don't store card details)</li>
          </ul>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">2. How We Use Your Information</h3>
          <p className="mb-2">We use your data to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide and maintain the MeetingPro service</li>
            <li>Process your meeting notes and generate professional follow-ups</li>
            <li>Manage your account, subscription, and billing</li>
            <li>Improve our service and develop new features</li>
            <li>Send important service updates and security notifications</li>
            <li>Provide customer support</li>
          </ul>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">3. How We Store Your Data</h3>
          <p className="mb-2">Your data is stored securely using:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Supabase:</strong> Database hosting with encryption at rest</li>
            <li><strong>Vercel:</strong> Application hosting on secure infrastructure</li>
            <li><strong>TLS/SSL:</strong> All data transmitted using industry-standard encryption</li>
            <li><strong>Regular backups:</strong> Automatic daily backups for data recovery</li>
          </ul>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">4. Data Sharing and Third Parties</h3>
          <p className="mb-2">We share your data only with essential service providers:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Anthropic Claude API:</strong> To generate meeting follow-ups (your data is not stored or used for training by Anthropic)</li>
            <li><strong>Stripe:</strong> For secure payment processing (see Stripe's privacy policy)</li>
            <li><strong>Supabase:</strong> For secure data storage (see Supabase's privacy policy)</li>
          </ul>
          <p className="mt-2"><strong>We do NOT:</strong></p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Sell your data to third parties</li>
            <li>Use your meeting notes to train AI models</li>
            <li>Share your data for advertising purposes</li>
            <li>Use tracking cookies or analytics beyond essential functionality</li>
          </ul>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">5. Your Rights and Choices</h3>
          <p className="mb-2">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Access:</strong> View all your data anytime through your account</li>
            <li><strong>Delete:</strong> Delete individual meetings or your entire account</li>
            <li><strong>Export:</strong> Request a copy of your data (contact support@meetingpro.app)</li>
            <li><strong>Correct:</strong> Update your account information</li>
            <li><strong>Object:</strong> Opt out of non-essential communications</li>
          </ul>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">6. Data Retention</h3>
          <p>We retain your data while your account is active. After account deletion, we permanently delete all your meeting notes within 30 days. We may retain minimal account information for legal compliance.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">7. Security</h3>
          <p>We implement industry-standard security measures including encryption, secure authentication, regular security audits, and limited employee access. However, no system is 100% secure.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">8. Cookies and Tracking</h3>
          <p>We use only essential cookies for authentication and session management. We do not use advertising or tracking cookies.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">9. Children's Privacy</h3>
          <p>MeetingPro is not intended for users under 18. We don't knowingly collect data from children.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">10. International Users</h3>
          <p>Your data may be processed in the United States or other countries where our service providers operate. By using MeetingPro, you consent to this transfer.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">11. Changes to Privacy Policy</h3>
          <p>We may update this policy. We'll notify you of significant changes via email. Your continued use after changes means you accept the updated policy.</p>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 text-base mb-2">12. Contact Us</h3>
          <p>Questions about privacy or want to exercise your rights? Email us at support@meetingpro.app</p>
        </div>
      </div>
      
      <button
        onClick={() => setShowPrivacy(false)}
        className="mt-6 w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700"
      >
        Close
      </button>
    </div>
  </div>
)}
    </div>
  );
}